import type { GenericFieldConfig } from '@neo/blocks/forms/types'
import { Button } from '@neo/primitives/button'
import { Label } from '@neo/primitives/label'
import { Select } from '@neo/primitives/select'
import { Lock } from 'lucide-react'
import { openBuddyEnterpriseRequest } from 'v2/shared/utils/buddy-enterprise-request'
import { getLocalAuth } from 'v2/utils'
import { z } from 'zod'
import {
  defaultOutboundAuthType,
  getRegionPlatformOptions,
  isInboundOnlyPlatform,
  SIP_PLATFORMS,
  type SipPlatformKey,
} from './platform-config'
import { PlatformLogo } from './platform-logo'
import type { UnifiedApplicationFormValues } from './types'

export interface PlatformGateOptions {
  /** Closes the sheet hosting the form, so the Buddy panel is not covered */
  onCloseHost?: () => void
  /** Custom SIP Platform is enterprise-gated for new signups (flag unlocks existing ones) */
  isCustomPlatformLocked?: boolean
  /**
   * Messaging on inbound trunks is Enterprise-only per the platform-aware
   * redesign — the section is fully hidden (not shown-locked) for other plans.
   * Saved message_url values are preserved: defaults still carry them into the
   * update payload even when the section is hidden.
   */
  isMessagingLocked?: boolean
}

/**
 * The one state the console must refuse to save: creating a SIP trunk on the locked
 * Custom platform. The option is selectable by design (that is how the explanation
 * card is reached), the gated fields below are unmounted so they validate to nothing,
 * and `sip_platform`'s enum accepts 'custom' regardless -- so without this the
 * footer's Create Trunk stays enabled and the trunk is created, then recorded as
 * custom (caught in review by Ashish, 2026-08-05). There is no server-side
 * enforcement, which makes the console the enforcement.
 *
 * Never true while EDITING, and that clause is load-bearing rather than mere
 * grandfathering. resolveTrunkPlatform falls back to 'custom' for any trunk with no
 * record and no recognisable URI or managed ACL, so a locked account can legitimately
 * be looking at a "custom" trunk it must still be able to rename or re-auth: one
 * created through the public API (never recorded, and outbound has no URI), one
 * created before platform recording shipped, one left behind by an Enterprise
 * downgrade, or one built while customSipPlatformEnabled was on. Blocking edit would
 * disable Update on those forever, which is worse than what it prevents.
 */
export const isLockedCustomPlatform = (
  values: UnifiedApplicationFormValues,
  isEditing: boolean,
  isCustomPlatformLocked: boolean,
): boolean =>
  !isEditing &&
  isCustomPlatformLocked &&
  values.application_type === 'sip' &&
  values.sip_platform === 'custom'

/**
 * The platform gate: SIP trunk fields below the platform select stay hidden
 * until a usable platform is chosen. Editing bypasses the gate entirely —
 * the lock applies to CREATING custom trunks only, never to existing config.
 */
export const isPlatformGateOpen = (
  values: UnifiedApplicationFormValues,
  isEditing: boolean,
  isCustomPlatformLocked: boolean,
): boolean => {
  if (values.application_type !== 'sip') return false
  if (isEditing) return true
  if (!values.sip_platform) return false
  return values.sip_platform !== 'custom' || !isCustomPlatformLocked
}

export const getSipPlatformFields = (
  isEditing: boolean,
  isCustomPlatformLocked: boolean,
  /** Dismisses the host sheet before the Buddy panel opens behind it */
  onCloseHost?: () => void,
  /**
   * The sheet's isOutbound sync, normally fed by the direction radio's
   * afterOnChange. Picking an inbound-only platform force-flips the direction
   * programmatically, which never reaches afterOnChange (custom fields bypass
   * it), so the flip has to report itself through the same callback.
   */
  onTrunkDirectionChange?: (isOutbound: boolean) => void,
): GenericFieldConfig<UnifiedApplicationFormValues> => [
  {
    name: 'sip_platform',
    type: 'custom',
    className: 'col-span-2',
    onChangeListenTo: ['application_type'],
    // Visible while editing too, because the platform decides what config is
    // legal and that has to hold past creation. Editable there as well: the
    // console can re-point a trunk's URI, ACL and credentials, so switching
    // platform is a real migration path that keeps the trunk domain and its
    // linked numbers, and it is the only way to fix a trunk recorded wrong.
    visible: (values) => values.application_type === 'sip',
    component: ({ form }) => {
      const sipPlatform = form.getFieldValue('sip_platform') as SipPlatformKey | undefined
      // Account data region, the same signal that picks the ElevenLabs India
      // residency host. Read at render rather than captured in the factory, so it
      // follows the session instead of whenever the config was built.
      const region = getLocalAuth().region

      const options = getRegionPlatformOptions(region, sipPlatform).map((key) => {
        const platform = SIP_PLATFORMS[key]
        // Existing trunks are grandfathered (the gate is create-only), so never
        // locked while editing.
        const isLocked = key === 'custom' && isCustomPlatformLocked && !isEditing
        return {
          // Per Tejal's design: the locked option stays SELECTABLE and carries a lock
          // marker; picking it keeps the rest of the form gated and explains why in a
          // card underneath. Not ghosted and not disabled, because selecting it is
          // how you reach the upgrade request.
          label: (
            <div className="flex items-center gap-2 w-full min-w-0">
              <PlatformLogo platform={key} />
              {/* no flex-1 on the label: it would push the lock to the far edge of the
                  row, and the lock belongs directly beside the name it qualifies */}
              <span className="min-w-0 truncate text-p-500">{platform.label}</span>
              {isLocked && <Lock className="h-3 w-3 flex-shrink-0 text-secondary" />}
            </div>
          ),
          value: key,
          searchValue: platform.label,
        }
      })

      return (
        <div className="flex flex-col gap-2" data-testid="select-sip-platform">
          <Label>SIP Platform</Label>
          <Select
            options={options}
            value={sipPlatform || ''}
            // 7 platforms crossed the Select's >6 Virtuoso threshold; this is a
            // short static list that must render in full, not a 200px scroller
            virtualized={false}
            placeholder="Select the SIP platform"
            onChange={(value) => {
              const newValue = (Array.isArray(value) ? value[0] : value) as SipPlatformKey | ''
              if (newValue && newValue !== sipPlatform) {
                form.setFieldValue('sip_platform', newValue)
                form.validateField('sip_platform', 'change')
                // Side effects live HERE, not in afterOnChange — the form lib only
                // fires afterOnChange through the component's onChange prop, which
                // custom Selects bypass. Platform switch invalidates prior
                // selections (URIs belong to the old platform's filtered list) and
                // each platform has a fixed outbound auth: managed → Plivo ACL,
                // LiveKit → credentials only, custom → user picks.
                //
                // clearField backfills errorMap after each clear: TanStack 0.28's
                // setFieldValue on a NOT-YET-MOUNTED field (URI fields while on
                // outbound, and vice versa) creates meta WITHOUT errorMap; the
                // field later mounts keeping the broken meta and the first linked
                // validation crashes reading errorMap['onChange'] (repro pinned in
                // outbound-auth-card.test 'direction toggling').
                const clearField = (
                  name:
                    | 'primary_uri'
                    | 'fallback_uri'
                    | 'ipacl_uuid'
                    | 'credential_uuid'
                    | 'outbound_uri',
                ) => {
                  form.setFieldValue(name, undefined)
                  form.setFieldMeta(name, (prev) => ({
                    ...prev,
                    errorMap: prev?.errorMap ?? {},
                    errors: prev?.errors ?? [],
                    isTouched: false,
                  }))
                }
                clearField('primary_uri')
                clearField('fallback_uri')
                clearField('ipacl_uuid')
                clearField('credential_uuid')
                // also nudges the hidden LiveKit outbound URI field to re-check
                // its visibility: hidden fields only re-render when their own
                // value changes or a parent re-renders, and platform pick
                // otherwise touches nothing on it
                clearField('outbound_uri')
                form.setFieldValue('trunk_authentication_type', defaultOutboundAuthType(newValue))
                form.setFieldMeta('trunk_authentication_type', (prev) => ({
                  ...prev,
                  errorMap: prev?.errorMap ?? {},
                  errors: prev?.errors ?? [],
                  isTouched: false,
                }))
                // Inbound-only platform picked while the form sits on outbound
                // (or before the radio ever mounted): retarget the direction,
                // since the radio only offers Inbound for it. Meta backfill per
                // the never-mounted-field rule; the callback keeps the sheet's
                // isOutbound in sync (afterOnChange never fires for
                // programmatic setFieldValue).
                if (
                  isInboundOnlyPlatform(newValue as SipPlatformKey) &&
                  form.getFieldValue('trunk_direction') !== 'inbound'
                ) {
                  form.setFieldValue('trunk_direction', 'inbound')
                  form.setFieldMeta('trunk_direction', (prev) => ({
                    ...prev,
                    errorMap: prev?.errorMap ?? {},
                    errors: prev?.errors ?? [],
                  }))
                  onTrunkDirectionChange?.(false)
                }
              }
            }}
            selectedValueRenderer={({ value }) => {
              const key = (Array.isArray(value) ? value[0] : String(value)) as SipPlatformKey
              if (!SIP_PLATFORMS[key]) return <span />
              // The lock stays visible on the closed select, not just in the list, so
              // the reason the form below is gated is still on screen (Tejal's design).
              const showLock = key === 'custom' && isCustomPlatformLocked && !isEditing
              return (
                <span className="flex items-center gap-2">
                  <PlatformLogo platform={key} />
                  {SIP_PLATFORMS[key].label}
                  {showLock && <Lock className="h-3 w-3 flex-shrink-0 text-secondary" />}
                </span>
              )
            }}
          />
        </div>
      )
    },
    zodValidation: (values) =>
      values.application_type === 'sip' && !isEditing
        ? z.enum(['livekit', 'retell', 'vapi', 'elevenlabs', 'xai', 'openai', 'custom'], {
            required_error: 'SIP platform is required',
          })
        : z.any(),
  },
  {
    name: 'custom_platform_upgrade_card',
    type: 'basic',
    component: ({ form }) => {
      const applicationType = form.useStore((state) => state.values.application_type)
      const sipPlatform = form.useStore(
        (state) => state.values.sip_platform as SipPlatformKey | undefined,
      )
      if (applicationType !== 'sip' || isEditing) return null
      if (sipPlatform !== 'custom' || !isCustomPlatformLocked) return null

      return (
        <div
          className="col-span-2 bg-bg3 rounded-md p-6 flex flex-col items-center text-center gap-2"
          data-testid="card-custom-platform-locked"
        >
          <span className="text-p-500 text-foreground">Enterprise Plan Required</span>
          <span className="text-s-400 text-secondary">
            Connect your own SIP infrastructure or any other SIP platform.
          </span>
          <Button
            type="button"
            className="mt-2"
            onClick={() => {
              // Close the sheet first, otherwise the Buddy panel opens behind this
              // overlay and looks like nothing happened (same order as the Messaging
              // tab gate in unified-form.config). Passed in rather than pulled from
              // useSheet: this config module must not require sheet context, or it
              // cannot render outside a sheet (its own regression tests caught that).
              onCloseHost?.()
              openBuddyEnterpriseRequest({
                source: 'voice-feature',
                featureLabel: 'Custom SIP Platform',
              })
            }}
            data-testid="button-request-upgrade-custom-sip"
          >
            Request Upgrade
          </Button>
        </div>
      )
    },
  },
]
